import { useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import { getQueryParams, parseCommaNumber, parseCommaString } from "@/utils/query";
import dayjs from "dayjs";
import { useAppDispatch } from "@/store/hooks";
import {
  fetchMonthlyAvailability,
  setTargetDateAvailability,
} from "@/store/slices/availabilitySlice";
import DailyCombinationSection from "@/components/combinations/DailyCombinationSection";
import styled from "styled-components";

function buildSearch(params: Record<string, string | undefined>) {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== "") {
      searchParams.set(key, value);
    }
  });

  return `?${searchParams.toString()}`;
}
const Container = styled.div`
    padding-top: 200px;
`
const Wrap = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
`
export default function DailySchedule() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const query = getQueryParams(location.search);

  const {
    selectedShopIds: selectedShopIdsStr = "",
    fixedVendorIds: fixedVendorIdsStr = "",
    tourSize = "3",
    selectedDate: selectedDateStr = dayjs().format("YYYY-MM-DD"),
  } = query;

  const selectedShopIds = useMemo(
    () => parseCommaString(selectedShopIdsStr),
    [selectedShopIdsStr]
  );

  const fixedShopIds = useMemo(
    () => parseCommaNumber(fixedVendorIdsStr),
    [fixedVendorIdsStr]
  );

  const { shops } = useSelector((state: RootState) => state.shop);
  const { dailyAvailCountMap, customerName, customerPhone } = useSelector(
    (state: RootState) => state.availability
  );

  const selectedVendors = useMemo(() => {
    return shops.filter((shop) => selectedShopIds.includes(String(shop.id)));
  }, [shops, selectedShopIds]);

  const currentDate = useMemo(() => {
    const parsed = dayjs(selectedDateStr, "YYYY-MM-DD", true);
    return parsed.isValid() ? parsed.toDate() : dayjs().toDate();
  }, [selectedDateStr]);

  const getWeekDates = (date: Date) => {
    const day = date.getDay();
    const diff = date.getDate() - day;
    const sunday = new Date(date);
    sunday.setDate(diff);

    const week = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(sunday);
      d.setDate(sunday.getDate() + i);
      week.push(d);
    }
    return week;
  };

  const weekDates = useMemo(() => getWeekDates(currentDate), [currentDate]);

  const requiredMonthKeys = useMemo(() => {
    return [...new Set(weekDates.map((date) => dayjs(date).format("YYYY-MM")))];
  }, [weekDates]);

  const currentDateKey = dayjs(currentDate).format("YYYY-MM-DD");

  const updateDailyScheduleQuery = (next: {
    selectedDate?: string;
    selectedShopIds?: string[];
    fixedVendorIds?: string;
    tourSize?: string;
  }) => {
    navigate(
      {
        pathname: location.pathname,
        search: buildSearch({
          selectedShopIds: (next.selectedShopIds ?? selectedShopIds).join(","),
          fixedVendorIds: next.fixedVendorIds ?? fixedVendorIdsStr,
          tourSize: next.tourSize ?? tourSize,
          selectedDate: next.selectedDate ?? currentDateKey,
        }),
      },
      { replace: true }
    );
  };

  const changeWeek = (direction: number) => {
    const nextDate = dayjs(currentDate)
      .add(direction * 7, "day")
      .format("YYYY-MM-DD");

    updateDailyScheduleQuery({ selectedDate: nextDate });
  };

  const selectDate = (date: Date) => {
    updateDailyScheduleQuery({
      selectedDate: dayjs(date).format("YYYY-MM-DD"),
    });
  };

  const getWeekNumber = (date: Date) => {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
    const days = Math.floor(
      (date.getTime() - firstDay.getTime()) / (24 * 60 * 60 * 1000)
    );
    return Math.ceil((days + firstDay.getDay() + 1) / 7);
  };

  const monthNames = [
    "1월",
    "2월",
    "3월",
    "4월",
    "5월",
    "6월",
    "7월",
    "8월",
    "9월",
    "10월",
    "11월",
    "12월",
  ];

  const dayNames = ["일", "월", "화", "수", "목", "금", "토"];

  useEffect(() => {
    if (selectedShopIds.length === 0) return;

    requiredMonthKeys.forEach((monthKey) => {
      const hasMissingDate = weekDates
        .filter((date) => dayjs(date).format("YYYY-MM") === monthKey)
        .some((date) => {
          const fullDateKey = dayjs(date).format("YYYY-MM-DD");
          return dailyAvailCountMap[fullDateKey] === undefined;
        });

      if (hasMissingDate) {
        dispatch(
          fetchMonthlyAvailability({
            month: monthKey,
            tourSize,
            selectedShopIds,
            fixedShopIds,
          })
        );
      }
    });
  }, [
    dispatch,
    requiredMonthKeys,
    weekDates,
    tourSize,
    selectedShopIdsStr,
    fixedVendorIdsStr,
    dailyAvailCountMap,
  ]);
  
  return (
    <Container className="min-h-screen bg-gray-50 flex flex-col mx-auto max-w-4xl border-x border-gray-200">
      <Wrap className="bg-white px-5 py-4 shadow-sm border-b border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-xl text-gray-700"></i>
          </button>

          <div className="flex items-center gap-3">
            <button
              onClick={() => changeWeek(-1)}
              className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-lg text-gray-700"></i>
            </button>

            <h2 className="text-sm font-bold text-gray-900 whitespace-nowrap">
              {currentDate.getFullYear()}년 {monthNames[currentDate.getMonth()]}{" "}
              {getWeekNumber(currentDate)}주차
            </h2>

            <button
              onClick={() => changeWeek(1)}
              className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-lg text-gray-700"></i>
            </button>
          </div>

          <div className="w-9"></div>
        </div>

        <div className="flex gap-2 overflow-x-auto pb-2 -mx-5 px-5">
          {weekDates.map((date, index) => {
            const fullDateKey = dayjs(date).format("YYYY-MM-DD");
            const isSelected = fullDateKey === currentDateKey;
            const count = dailyAvailCountMap[fullDateKey] || 0;
            const hasAvailability = count > 0;

            return (
              <button
                key={index}
                onClick={() => hasAvailability && selectDate(date)}
                disabled={!hasAvailability}
                className={`flex-shrink-0 py-3 rounded-xl flex flex-col justify-center items-center gap-1 transition-all ${
                  isSelected
                    ? "bg-blue-600 text-white shadow-md"
                    : hasAvailability
                    ? "border border-gray-300 text-gray-700 hover:bg-gray-50 cursor-pointer"
                    : "bg-gray-100 text-gray-400 cursor-not-allowed"
                }`}
                style={{ width: `calc((100% - 3rem) / 7)` }}
              >
                <span className="text-xs font-medium">
                  {dayNames[date.getDay()]}
                </span>
                <span className="text-lg font-bold">{date.getDate()}</span>
                {hasAvailability && (
                  <span
                    className={`text-xs font-medium ${
                      isSelected ? "text-blue-100" : "text-blue-600"
                    }`}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </Wrap>

      <div className="bg-white px-5 py-3 border-b border-gray-100">
        <h1 className="text-2xl font-bold text-gray-900">
          {currentDate.getMonth() + 1}월 {currentDate.getDate()}일 (
          {dayNames[currentDate.getDay()]})
        </h1>
        <p className="text-sm text-blue-600 font-medium mt-1">
          가능 조합 {dailyAvailCountMap[currentDateKey] || 0}개
        </p>
      </div>

      <div className="p-5">
        <DailyCombinationSection
          date={currentDateKey}
          selectedShopIds={selectedVendors.map((shop) => shop.id)}
          tourSize={tourSize}
          customerName={customerName}
          customerPhone={customerPhone}
          onCountChange={(count) => {
            dispatch(
              setTargetDateAvailability({
                date: currentDateKey,
                count,
              })
            );
          }}
        />
      </div>
    </Container>
  );
}